import { dramaVersion } from './dramaMedia'

/**
 * 互动小说视频桥接（影游）字段处理
 * 接口契约见《互动小说视频桥接C端接口》：命中 Nacos 配置的互动小说内容对象会追加
 * play_mode='VIDEO_BRIDGE' 与 video_entry（snake_case），其余字段沿用原接口 camelCase。
 */
import { PLAY_MODE } from '@/constants'

export interface VideoBridgeEntry {
  release_code: string
  oss_root: string
  /** 相对或完整的封面/入口视频资源路径，为空时用 C 端默认展示 */
  cover_oss_url?: string
  /** JSON 文本（非数组），需 JSON.parse；解析失败降级为空列表 */
  cast_json?: string
  /** 播放器加载图（相对 oss_root） */
  loading_image?: string
}

export interface VideoBridgeCast {
  /** 人物头像（相对 oss_root 或完整 URL） */
  path: string
  actor: string
  /** 人物简介 */
  intro?: string
  short_intro_en?: string
  short_intro_zh?: string
  tags?: string[]
}

/** 内容对象是否命中视频桥接（进入影游播放路径） */
export function isVideoBridge<T>(item: T): item is T & { video_entry: VideoBridgeEntry } {
  const candidate = item as { play_mode?: string; video_entry?: VideoBridgeEntry } | null | undefined
  return candidate?.play_mode === PLAY_MODE.VIDEO_BRIDGE && !!candidate?.video_entry?.oss_root
}

/** 拼接 oss_root 与相对路径；path 本身是完整 URL 时直接返回 */
export function resolveVideoBridgeUrl(entry: VideoBridgeEntry | undefined, path?: string): string {
  if (!entry || !path)
    return ''
  if (/^https?:\/\//.test(path))
    return path
  return `${entry.oss_root.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`
}

/** 入口视频地址（oss_root + cover_oss_url） */
export function videoBridgeVideoUrl(entry: VideoBridgeEntry | undefined): string {
  return resolveVideoBridgeUrl(entry, entry?.cover_oss_url)
}

/**
 * A 书保留 save:drama:<集数>；新书按 drama:book:<contentId>:v1:save:<集数> 隔离。
 * 播放器每进入一集写入一条，取最大集数即当前观看进度（只到章节粒度）。
 * 本地开发与播放器不同源读不到，返回 0（未开始）不影响展示。
 */

/** 本地推算的影游观看进度：已观看到的最大集数，0 = 未开始 */
export function dramaLocalEpisode(contentId: string, entry?: VideoBridgeEntry): number {
  if (!contentId)
    return 0
  const prefix = dramaVersion(entry?.oss_root) === 'A' ? 'save:drama:' : `drama:book:${contentId}:v1:save:`
  try {
    let max = 0
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      const matched = key?.startsWith(prefix) ? key.slice(prefix.length).match(/^(\d+)$/) : null
      if (matched)
        max = Math.max(max, Number(matched[1]))
    }
    return max
  }
  catch {
    return 0
  }
}

/** 解析演员表，失败降级为空数组，不阻断播放 */
export function parseVideoBridgeCast(entry: VideoBridgeEntry | undefined): VideoBridgeCast[] {
  if (!entry?.cast_json)
    return []
  try {
    const cast = JSON.parse(entry.cast_json)
    return Array.isArray(cast) ? cast.filter(item => item && item.path) : []
  }
  catch {
    return []
  }
}
